/* eslint-disable jsdoc/require-jsdoc */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React from 'react';
import { EventSourcePlus } from 'event-source-plus';
import { subMinutes, subHours, subDays } from 'date-fns';
import downloadjs from 'downloadjs';
import {
  ClientThresholdSettingsAttribute,
  DashboardDataFilter,
  DeviceSummaryAttribute,
  DeviceType,
  Group,
  ParametersType,
  ParticleData,
  PriorityList,
  summaryOverTimeResponse,
  ThresholdConfig,
  TimeSpan,
  TimeSpanDateAttributes,
  UserConfigV2
} from '../constants/telosair-interfaces';
import { DEFAULT_START_TIME, DEFAULT_UNITS, defaultThreshold } from '../constants/global-constants';
import { PARAMETER_CONVERSION } from './UnitCalculators';

import { DEV_NEW_DOMAIN_URL } from '../environment';
import { getTokenFromSessionStorage } from '../services/session-storage-service';

/**
 * Handles time span change.
 * @param span - Time span selected.
 * @param updatedDate - UpdatedTime/ Date.
 * @param updatedDate.startDate - Start date.
 * @param updatedDate.endDate - End date.
 * @returns - Date obj.
 */
export function handleTimeSpanChange(
  span: string | TimeSpan,
  updatedDate: TimeSpanDateAttributes | null
): any {
  const date: { startDate?: number; endDate?: number; custom?: boolean } = { ...updatedDate };
  if (span === TimeSpan.RealTime) {
    date.startDate = subMinutes(new Date(), DEFAULT_START_TIME).getTime();
    date.endDate = new Date().getTime();
  } else if (span === TimeSpan.Mins15) {
    date.startDate = subMinutes(new Date(), 15).getTime();
    date.endDate = new Date().getTime();
  } else if (span === TimeSpan.Hr1) {
    date.startDate = subMinutes(new Date(), 60).getTime();
    date.endDate = new Date().getTime();
  } else if (span === TimeSpan.Hrs4) {
    date.startDate = subHours(new Date(), 4).getTime();
    date.endDate = new Date().getTime();
  } else if (span === TimeSpan.Hrs24) {
    date.startDate = subHours(new Date(), 24).getTime();
    date.endDate = new Date().getTime();
  } else if (span === TimeSpan.Week1) {
    date.startDate = subDays(new Date(), 7).getTime();
    date.endDate = new Date().getTime();
  } else if (span === TimeSpan.Month1) {
    date.startDate = subDays(new Date(), 30).getTime();
    date.endDate = new Date().getTime();
  } else if (span === TimeSpan.Custom) {
    date.custom = true;
  }

  return date;
}

const findConfig = (configs: ThresholdConfig[], id: string | undefined): ThresholdConfig => {
  if (!id) {
    return defaultThreshold;
  }
  return configs.find((config) => config.id === id) as ThresholdConfig;
};

export const getActiveConfig = (
  clientSetting: ClientThresholdSettingsAttribute | null,
  type: DeviceType
): ThresholdConfig => {
  if (type === DeviceType.INDOOR) {
    return (
      findConfig(clientSetting?.userCreatedConfigs || [], clientSetting?.indoorConfigId) ||
      defaultThreshold
    );
  }
  if (type === DeviceType.OUTDOOR) {
    return (
      findConfig(clientSetting?.userCreatedConfigs || [], clientSetting?.outdoorConfigId) ||
      defaultThreshold
    );
  }
  return (
    findConfig(clientSetting?.userCreatedConfigs || [], clientSetting?.mixedConfigId) ||
    defaultThreshold
  );
};

/**
 * Convert timeSpan to milliseconds.
 * @param span - Selected time span.
 * @returns - Milliseconds.
 */
export function getMillSecsByTimeSpan(span: TimeSpan): any {
  let millSecs = 0;
  if (span === TimeSpan.RealTime) {
    millSecs = 15 * 60 * 1000;
  } else if (span === TimeSpan.Mins15) {
    millSecs = 15 * 60 * 1000;
  } else if (span === TimeSpan.Hr1) {
    millSecs = 1 * 60 * 60 * 1000;
  } else if (span === TimeSpan.Hrs4) {
    millSecs = 4 * 60 * 60 * 1000;
  } else if (span === TimeSpan.Hrs24) {
    millSecs = 24 * 60 * 60 * 1000;
  } else if (span === TimeSpan.Week1) {
    millSecs = 7 * 24 * 60 * 60 * 1000;
  } else if (span === TimeSpan.Month1) {
    millSecs = 30 * 24 * 60 * 60 * 1000;
  } else if (span === TimeSpan.Custom) {
    millSecs = 15 * 60 * 1000;
  }

  return millSecs;
}

/**
 * Gives difference in mill seconds between two time span.
 * @param timeSpanDate - Time span date object.
 * @returns Difference.
 */
export function getMillSecondsDiffrenceBetweenTimes(timeSpanDate: TimeSpanDateAttributes): number {
  const date1 = new Date(timeSpanDate.endDate);
  const date2 = new Date(timeSpanDate.startDate);

  const time1Ms = date1.getTime();
  const time2Ms = date2.getTime();

  const differenceMs = Math.abs(time1Ms - time2Ms);
  return differenceMs;
}

/**
 * Function to get priority based on min and max value.
 * @param value - Value of current parameter.
 * @param lowerCritical - LowerCritical.
 * @param lowerWarning - LowerWarning.
 * @param upperWarning  - UpperWarning.
 * @param upperCritical  - UpperCritical.
 * @param max  - UpperWarning.
 * @param min  - UpperCritical.
 * @returns - Priority of the device.
 */
const getPriority = (
  value: number,
  lowerCritical: number,
  lowerWarning: number,
  upperWarning: number,
  upperCritical: number,
  max: number,
  min: number
): PriorityList => {
  if (value > max || value < min) {
    return PriorityList.high;
  }
  if (value < lowerCritical && value >= min) return PriorityList.high;
  if (value >= lowerCritical && value < lowerWarning) return PriorityList.medium;
  if (value >= lowerWarning && value <= upperWarning) return PriorityList.low;
  if (value > upperWarning && value <= upperCritical) return PriorityList.medium;
  if (value >= upperCritical && value <= max) return PriorityList.high;
  return PriorityList.none;
};

/**
 * Converts a threshold value from the parameter's default unit into the unit the
 * user picked in Settings -> Units.
 *
 * Thresholds are the only numbers the client converts. Readings returned by the
 * dashboard APIs are already expressed in the user's selected unit - the backend
 * applies the same `config.unit` map (ConversionUtils.unitConversion) before
 * responding - so converting a reading here as well applies the formula twice
 * (25 C was rendered as 170.6 F instead of 77 F).
 * @param value - Threshold value in the parameter's default unit.
 * @param selectedUnit - Selected unit.
 * @param parameter - Selected parameter.
 * @returns - Converted value.
 */
const convertThresholdValueToSelectedUnit = (
  value: number,
  selectedUnit: string,
  parameter: ParametersType
): number => {
  if (!selectedUnit || DEFAULT_UNITS[parameter] === selectedUnit) {
    return value;
  }
  if (!PARAMETER_CONVERSION[parameter]) {
    return value;
  }
  return PARAMETER_CONVERSION[parameter](value, selectedUnit);
};

/**
 * Hook to get color of status by given parameter.
 * @param key - Key of parameter.
 * @param clientSetting - ClientSetting.
 * @param userConfigDataV2 - User config data version 2.
 * @param type - Type of config.
 * @returns - Thresholds.
 */
export const getThresholds = (
  key: ParametersType,
  clientSetting: ClientThresholdSettingsAttribute | null,
  userConfigDataV2: UserConfigV2 | null,
  type: DeviceType
): {
  lowerCritical: number;
  lowerWarning: number;
  upperWarning: number;
  upperCritical: number;
  max: number;
  min: number;
} => {
  const selectedUnit = userConfigDataV2?.config?.unit && userConfigDataV2?.config?.unit[key];
  const activeConfig: ThresholdConfig = getActiveConfig(clientSetting, type);

  const lowerCriticalDefaultValue = activeConfig.thresholds[key]?.lowerCritical;
  const lowerWarningDefaultValue = activeConfig.thresholds[key]?.lowerWarning;
  const upperWarningDefaultValue = activeConfig.thresholds[key]?.upperWarning;
  const upperCriticalDefaultValue = activeConfig.thresholds[key]?.upperCritical;
  const maxDefaultValue = activeConfig.thresholds[key]?.max;
  const minDefaultValue = activeConfig.thresholds[key]?.min;

  const lowerCritical = selectedUnit
    ? convertThresholdValueToSelectedUnit(lowerCriticalDefaultValue, selectedUnit, key)
    : lowerCriticalDefaultValue;
  const lowerWarning = selectedUnit
    ? convertThresholdValueToSelectedUnit(lowerWarningDefaultValue, selectedUnit, key)
    : lowerWarningDefaultValue;
  const upperWarning = selectedUnit
    ? convertThresholdValueToSelectedUnit(upperWarningDefaultValue, selectedUnit, key)
    : upperWarningDefaultValue;
  const upperCritical = selectedUnit
    ? convertThresholdValueToSelectedUnit(upperCriticalDefaultValue, selectedUnit, key)
    : upperCriticalDefaultValue;
  const max = selectedUnit
    ? convertThresholdValueToSelectedUnit(maxDefaultValue, selectedUnit, key)
    : maxDefaultValue;
  const min = selectedUnit
    ? convertThresholdValueToSelectedUnit(minDefaultValue, selectedUnit, key)
    : minDefaultValue;
  return { lowerCritical, lowerWarning, upperWarning, upperCritical, max, min };
};

/**
 * Hook to get color of status by given parameter.
 * @param key - Key of parameter.
 * @param value - Value of parameter.
 * @param clientSetting - ClientSetting.
 * @param userConfigDataV2 - User config data version 2.
 * @param type - Type of config.
 * @returns - Height and width of the element.
 */
export const getPriorityColor = (
  key: ParametersType,
  value: number | null,
  clientSetting: ClientThresholdSettingsAttribute | null,
  userConfigDataV2: UserConfigV2 | null,
  type: DeviceType
): PriorityList => {
  const selectedUnit = userConfigDataV2?.config?.unit && userConfigDataV2?.config?.unit[key];
  if (value === null || value === undefined || value === -9999) {
    return PriorityList.none;
  }
  const activeConfig: ThresholdConfig = getActiveConfig(clientSetting, type);

  const thresholdForKey = activeConfig.thresholds[key];
  // When a threshold has not been configured for this parameter (missing entry
  // or flagged `incomplete`), its bounds are left at 0, which would otherwise
  // make every real reading fall into the upper-critical branch and render red.
  // Treat unset thresholds as good (green) instead of alarming.
  if (!thresholdForKey || thresholdForKey.incomplete) {
    return PriorityList.low;
  }

  const lowerCriticalDefaultValue = thresholdForKey.lowerCritical;
  const lowerWarningDefaultValue = thresholdForKey.lowerWarning;
  const upperWarningDefaultValue = thresholdForKey.upperWarning;
  const upperCriticalDefaultValue = thresholdForKey.upperCritical;
  const maxDefaultValue = thresholdForKey.max;
  const minDefaultValue = thresholdForKey.min;

  const lowerCritical = selectedUnit
    ? convertThresholdValueToSelectedUnit(lowerCriticalDefaultValue, selectedUnit, key)
    : lowerCriticalDefaultValue;
  const lowerWarning = selectedUnit
    ? convertThresholdValueToSelectedUnit(lowerWarningDefaultValue, selectedUnit, key)
    : lowerWarningDefaultValue;
  const upperWarning = selectedUnit
    ? convertThresholdValueToSelectedUnit(upperWarningDefaultValue, selectedUnit, key)
    : upperWarningDefaultValue;
  const upperCritical = selectedUnit
    ? convertThresholdValueToSelectedUnit(upperCriticalDefaultValue, selectedUnit, key)
    : upperCriticalDefaultValue;
  const max = selectedUnit
    ? convertThresholdValueToSelectedUnit(maxDefaultValue, selectedUnit, key)
    : maxDefaultValue;
  const min = selectedUnit
    ? convertThresholdValueToSelectedUnit(minDefaultValue, selectedUnit, key)
    : minDefaultValue;

  const PRIORITY: PriorityList = getPriority(
    value,
    lowerCritical,
    lowerWarning,
    upperWarning,
    upperCritical,
    max,
    min
  );
  return PRIORITY;
};

/**
 * Function to generate datapoints by given timestamp.
 * @param startTime - Start time.
 * @param endTime - End time.
 * @param interval - Interval in minute.
 * @returns - Datapoints.
 */
export function generateDataPoints(startTime: number, endTime: number, interval: number): number[] {
  const dataPoints: number[] = [];
  for (let time = startTime; time <= endTime; time += interval * 60 * 1000) {
    dataPoints.push(new Date(time).getTime());
  }
  return dataPoints;
}

/**
 * Function to download a file.
 * @param data - Data to be downloaded.
 * @param name - Name of the file.
 * @param mime - Mime of the file.
 */
export const downloadFile = (data: any, name: string, mime: string): void => {
  downloadjs(data, name, mime);
};

/**
 * Getclasses function for getting the classess based on the priority level.
 * @param inp - That has to be passed to getclasses function.
 * @param priority - Priority of the parameter value.
 * @returns  the class.
 */
export const getclasses = (inp: boolean, priority: PriorityList): string => {
  if (priority === 'high') {
    return inp ? 'text-error-500' : 'bg-error-600 text-gray-300';
  }
  if (priority === 'medium') {
    return inp ? 'text-warning-500' : 'bg-warning-600 text-gray-300';
  }
  if (priority === 'low') {
    return inp ? 'text-success-500' : 'bg-success-600 text-gray-300';
  }
  if (priority === PriorityList.outOfRange) {
    return inp ? 'text-error-800' : 'bg-error-800 text-gray-300';
  }
  return 'text-gray-300';
};

// Define types for the function parameters
type SetupEventSourceParams = {
  loading: boolean;
  timeSpan: TimeSpan;
  selectedDevice: number | null;
  dataFilter: DashboardDataFilter | null;
  groupedData: Group[];
  dataSummary: ParticleData;
  setDataSummary: (data: ParticleData) => void;
  setGroupDataSummary: (data: { [key: string]: DeviceSummaryAttribute }) => void;
  updateGraphData: (data: any) => void;
  cleanGraphData: (data: any) => any;
};

// eslint-disable-next-line jsdoc/require-jsdoc
export const setupEventSource = ({
  loading,
  timeSpan,
  selectedDevice,
  dataFilter,
  groupedData,
  dataSummary,
  setDataSummary,
  setGroupDataSummary,
  updateGraphData,
  cleanGraphData
}: SetupEventSourceParams): any => {
  if (loading || timeSpan !== TimeSpan.RealTime) {
    return () => null;
  }

  let deviceList: number[] = [];
  if (selectedDevice) {
    deviceList = [selectedDevice];
  } else if (dataFilter?.type === 'group') {
    const group = groupedData.find((item) => item.groupId === dataFilter.id);
    if (group) {
      deviceList = group.sensorDetailsList.map((item) => item.serial_number);
    }
  }

  const deviceListTostring = deviceList.join(',');
  const eventSource = new EventSourcePlus(
    `${DEV_NEW_DOMAIN_URL}/dashboard/subscribe?sensors=${deviceListTostring}`,
    {
      headers: {
        Authorization: `Bearer ${getTokenFromSessionStorage()}`
      }
    }
  );

  const controller = eventSource.listen({
    // eslint-disable-next-line jsdoc/require-jsdoc
    onMessage(message: { data: string }) {
      const parsedData: {
        groupSummary: { [key: string]: DeviceSummaryAttribute };
        summary: DeviceSummaryAttribute;
        summaryOverTime: summaryOverTimeResponse;
      } = JSON.parse(message.data);

      if (parsedData.summary?.parameters && Object.keys(parsedData.summary.parameters).length > 1) {
        setDataSummary({ ...dataSummary, ...parsedData.summary.parameters });
      }
      if (parsedData.groupSummary && Object.keys(parsedData.groupSummary).length > 0) {
        setGroupDataSummary(parsedData.groupSummary);
      }
      if (parsedData.summaryOverTime?.xAxis?.length > 0) {
        updateGraphData(cleanGraphData(parsedData.summaryOverTime));
      }
    }
  });

  return () => controller.abort();
};

export const getPriorityText = (
  key: ParametersType,
  value: number | null,
  clientSetting: ClientThresholdSettingsAttribute | null,
  userConfigDataV2: UserConfigV2 | null,
  type: DeviceType
): string => {
  const selectedUnit = userConfigDataV2?.config?.unit && userConfigDataV2?.config?.unit[key];
  if (value === null || value === undefined || value === -9999) {
    return '';
  }
  const activeConfig: ThresholdConfig = getActiveConfig(clientSetting, type);

  const thresholdForKey = activeConfig.thresholds[key];
  // Unset/incomplete threshold: bounds are 0, so treat the reading as Optimal
  // rather than "Upper Critical" (mirrors getPriorityColor).
  if (!thresholdForKey || thresholdForKey.incomplete) {
    return 'Optimal';
  }

  const lowerCriticalDefaultValue = thresholdForKey.lowerCritical;
  const lowerWarningDefaultValue = thresholdForKey.lowerWarning;
  const upperWarningDefaultValue = thresholdForKey.upperWarning;
  const upperCriticalDefaultValue = thresholdForKey.upperCritical;

  const lowerCritical = selectedUnit
    ? convertThresholdValueToSelectedUnit(lowerCriticalDefaultValue, selectedUnit, key)
    : lowerCriticalDefaultValue;
  const lowerWarning = selectedUnit
    ? convertThresholdValueToSelectedUnit(lowerWarningDefaultValue, selectedUnit, key)
    : lowerWarningDefaultValue;
  const upperWarning = selectedUnit
    ? convertThresholdValueToSelectedUnit(upperWarningDefaultValue, selectedUnit, key)
    : upperWarningDefaultValue;
  const upperCritical = selectedUnit
    ? convertThresholdValueToSelectedUnit(upperCriticalDefaultValue, selectedUnit, key)
    : upperCriticalDefaultValue;

  if (value < lowerCritical) return 'Lower Critical';
  if (value < lowerWarning) return 'Lower Warning';
  if (value > upperCritical) return 'Upper Critical';
  if (value > upperWarning) return 'Upper Warning';

  return 'Optimal';
};
